
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Download } from 'lucide-react';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter,
  DialogClose
} from "@/components/ui/dialog";
import { getRecaptchaScriptUrl } from '@/utils/configService';
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";

const emailCaptureSchema = z.object({
  email: z.string().email("Voer een geldig e-mailadres in"),
  bedrijf: z.string().min(1, "Bedrijfsnaam is verplicht"),
  website: z.string().optional(), // Honeypot field
});

export type EmailCaptureFormValues = z.infer<typeof emailCaptureSchema>;

interface EmailCaptureDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: EmailCaptureFormValues) => void;
}

const EmailCaptureDialog: React.FC<EmailCaptureDialogProps> = ({ open, onOpenChange, onSubmit }) => {
  const form = useForm<EmailCaptureFormValues>({
    resolver: zodResolver(emailCaptureSchema),
    defaultValues: {
      email: "",
      bedrijf: "",
      website: "", // Initialize honeypot field as empty
    },
  });
  
  // Load reCAPTCHA script when component mounts
  useEffect(() => {
    if (open) {
      const script = document.createElement('script');
      script.src = getRecaptchaScriptUrl();
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
      
      return () => {
        // Remove script only if we added it
        const scriptElement = document.querySelector('script[src^="https://www.google.com/recaptcha/api.js"]');
        if (scriptElement && scriptElement.parentNode) {
          scriptElement.parentNode.removeChild(scriptElement);
        }
      };
    }
  }, [open]);

  // Add honeypot validation to form submission
  const handleSubmit = (data: EmailCaptureFormValues) => {
    // Check if this might be a spam submission
    if (data.website && data.website.length > 0) {
      console.log("Potential spam submission detected and blocked");
      return;
    }
    
    // Continue with normal submission for legitimate users
    onSubmit(data);
  };

  // Add honeypot field - hidden from real users
  const HoneypotField = () => (
    <div className="hidden" aria-hidden="true">
      <input
        type="text"
        tabIndex={-1}
        autoComplete="off"
        {...form.register("website")}
      />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Download Excel leadcalculator</DialogTitle>
          <DialogDescription>
            Vul je gegevens in om de volledige leadcalculator met marktcheck te ontvangen.
          </DialogDescription>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4 py-4">
            {/* Honeypot field - invisible to real users */}
            <HoneypotField />
            
            <div className="grid gap-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>E-mailadres*</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="jouw@email.be"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="bedrijf"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Bedrijfsnaam*</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Jouw bedrijf"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <div className="text-xs text-gray-500 mt-2">
              Deze site is beschermd door reCAPTCHA en de Google 
              <a href="https://policies.google.com/privacy" className="text-brand-blue hover:underline" target="_blank" rel="noopener noreferrer"> Privacybeleid</a> en
              <a href="https://policies.google.com/terms" className="text-brand-blue hover:underline" target="_blank" rel="noopener noreferrer"> Servicevoorwaarden</a> zijn van toepassing.
            </div>
            
            <DialogFooter className="mt-6 flex justify-between items-center gap-4">
              <DialogClose asChild>
                <Button type="button" variant="outline">Annuleren</Button>
              </DialogClose>
              <Button type="submit" className="flex items-center gap-2">
                <Download size={16} />
                Download nu
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default EmailCaptureDialog;
